import api from './api';
import { ClinicOnboardingData } from '@/pages/clinic-onboarding/types';

export interface ClinicOnboardingInviteDetails {
  email: string;
  clinicName: string;
  organisation: {
    id: string;
    name: string;
  };
  expiresAt: string;
  /** When the invited clinic is pre-created with an archetype (e.g. guided), used to hide Invite Practitioners step */
  clinicArchetype?: 'guided' | 'direct' | 'hybrid' | null;
  /** 'full' = owner completes entire wizard; 'partial' = owner only sets password + agreement + billing */
  onboardingType?: 'full' | 'partial';
  /** Pre-filled data from admin when onboardingType === 'partial' */
  partialData?: Record<string, unknown> | null;
  pendingSteps?: string[] | null;
  existingUser?: {
    firstName: string;
    lastName: string;
    hasPassword: boolean;
    isPractitioner?: boolean;
    practitionerId?: string | null;
    practitionerNpi?: string | null;
  } | null;
}

export interface NpiLookupAddress {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  phone: string;
  fax: string;
}

export interface NpiLookupResult {
  npi: string;
  entity_type: 'organization' | 'individual';
  organization_name?: string;
  first_name?: string;
  last_name?: string;
  address: NpiLookupAddress | null;
  taxonomies: Array<{
    code: string;
    desc: string;
    primary: boolean;
    state: string;
    license: string;
  }>;
  enumeration_date?: string;
  last_updated?: string;
}

export interface CompleteClinicOnboardingRequest {
  token: string;
  name: string;
  npi?: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  postal_code: string;
  primary_phone: string;
  fax?: string;
  email?: string;
  description: string;
  admin_first_name?: string;
  admin_last_name?: string;
  password?: string;
  skipPaymentSetup?: boolean;
  paymentData?: ClinicOnboardingData['paymentData'];
  practitionersToInvite?: Array<{
    email: string;
    first_name: string;
    last_name: string;
  }>;
  add_as_practitioner?: boolean;
  practitioner_npi?: string;
  practitioner_credentials?: string;
  practitioner_license_number?: string;
  practitioner_dea_number?: string;
  practitioner_phone?: string;
  practitioner_fax?: string;
  use_clinic_address_for_practitioner?: boolean;
  practitioner_address_line_1?: string;
  practitioner_address_line_2?: string;
  practitioner_city?: string;
  practitioner_state?: string;
  practitioner_postal_code?: string;
  existing_practitioner_id?: string;
  clinic_archetype?: 'direct' | 'guided' | 'hybrid';
  program_acknowledged?: boolean;
}

export interface CompleteClinicOnboardingResponse {
  success: boolean;
  message: string;
  data: {
    clinicId: string;
    clinicName: string;
    adminEmail: string;
    /** Stripe Connect onboarding URL for the clinic (when available) */
    stripeOnboardingUrl?: string;
    /** Raw token for billing step (when billingPending is true) */
    onboardingToken?: string;
    /** If true, frontend should show billing step and use onboardingToken for billing APIs */
    billingPending?: boolean;
  };
}

export interface CompletePartialOnboardingRequest {
  token: string;
  password: string;
  program_acknowledged: boolean;
}

export interface CompletePartialOnboardingResponse {
  success: boolean;
  message: string;
  data: {
    clinicId: string;
    clinicName: string;
    adminEmail: string;
    /** Token for the billing step */
    onboardingToken: string;
    billingPending: boolean;
  };
}

export const clinicOnboardingService = {
  validateInvite: async (token: string): Promise<{ success: boolean; data: ClinicOnboardingInviteDetails }> => {
    const response = await api.get('/clinic-onboarding/validate', {
      params: { token },
    });
    return response.data;
  },

  completeOnboarding: async (
    data: CompleteClinicOnboardingRequest,
  ): Promise<CompleteClinicOnboardingResponse> => {
    const response = await api.post('/clinic-onboarding/complete', data);
    return response.data;
  },

  completePartialOnboarding: async (
    data: CompletePartialOnboardingRequest,
  ): Promise<CompletePartialOnboardingResponse> => {
    const response = await api.post('/clinic-onboarding/complete-partial', data);
    return response.data;
  },

  checkNpiExists: async (npi: string): Promise<{ exists: boolean; clinic?: { id: string; name: string } }> => {
    try {
      const response = await api.get(`/public/clinics/check-npi/${npi}`);
      return response.data;
    } catch (error) {
      return { exists: false };
    }
  },

  lookupNpi: async (npi: string, token: string): Promise<NpiLookupResult | null> => {
    try {
      const response = await api.get(`/public/npi/lookup/${npi}`, {
        params: { token },
      });
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('NPI lookup failed:', error);
      return null;
    }
  },
};

export default clinicOnboardingService;
